import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import {
  useIsExchangeEnabled,
  useExchangeRates,
} from "./-components/is-exchange-enabled";
import { useEffect, useState } from "react";
import { useAnalytics } from "@/lib/analytics";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Dialog,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { X, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ExchangeCard } from "./-components/exchange-card";
import { payinAccountsWithAggregatedBalancesQuery } from "@/lib/queries";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

interface CurrencyRate {
  code: string;
  currency: string;
  flag: string;
  buyRate: number;
}

const baseCurrencies = [
  { code: "GBP", name: "British Pound", flag: "🇬🇧" },
  { code: "EUR", name: "Euro", flag: "🇪🇺" },
  { code: "USD", name: "US Dollar", flag: "🇺🇸" },
];

const baseCurrencyCodes = ["GBP", "EUR", "USD"];

export const Route = createFileRoute("/(app)/_main-layout/_exchange/exchange")({
  component: RouteComponent,
  loader: async ({ context }) => {
    return context.queryClient.ensureQueryData(
      payinAccountsWithAggregatedBalancesQuery(
        context.merchantId,
        context.accessToken,
        context.selectedApplicationId,
      ),
    );
  },
});

function RouteComponent() {
  const isExchangeEnabled = useIsExchangeEnabled();
  if (!isExchangeEnabled) {
    return <Navigate to="/" />;
  }

  return (
    <div>
      <Tabs value="exchange">
        <TabsList>
          <TabsTrigger value="exchange">Exchange</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mt-2">
        <ExchangePageComponent />
      </div>
    </div>
  );
}

function ExchangePageComponent() {
  const context = Route.useRouteContext();
  const balancesData = useSuspenseQuery(
    payinAccountsWithAggregatedBalancesQuery(
      context.merchantId,
      context.accessToken,
      context.selectedApplicationId,
    ),
  );
  const exchangeRates = useExchangeRates();

  const gbpBalanceAcrossAllAccounts = balancesData.data.reduce(
    (acc, curr) => (curr.currency === "GBP" ? acc + (curr.balance ?? 0) : acc),
    0,
  );
  const eurBalanceAcrossAllAccounts = balancesData.data.reduce(
    (acc, curr) => (curr.currency === "EUR" ? acc + (curr.balance ?? 0) : acc),
    0,
  );

  const analytics = useAnalytics();
  const [fromCurrency, setFromCurrency] = useState("GBP");
  const [toCurrency, setToCurrency] = useState("NGN");
  const [amount, setAmount] = useState("25000");
  const [convertedAmount, setConvertedAmount] = useState<string | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [isInterestedInProduct, setIsInterestedInProduct] = useState<
    boolean | null
  >(null);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [additionalCurrencies, setAdditionalCurrencies] = useState("");
  const [additionalFeedback, setAdditionalFeedback] = useState("");

  // Destination currency options
  const destinationCurrencies = [
    {
      id: "USD",
      label: "USD",
      description: "US Dollar",
      flag: "🇺🇸",
    },
    {
      id: "EUR",
      label: "EUR",
      description: "Euro",
      flag: "🇪🇺",
    },
    {
      id: "NGN",
      label: "NGN",
      description: "Nigerian Naira",
      flag: "🇳🇬",
    },
    {
      id: "KES",
      label: "KES",
      description: "Kenyan Shilling",
      flag: "🇰🇪",
    },
    {
      id: "GHS",
      label: "GHS",
      description: "Ghanaian Cedi",
      flag: "🇬🇭",
    },
    {
      id: "ZAR",
      label: "ZAR",
      description: "South African Rand",
      flag: "🇿🇦",
    },
    {
      id: "UGX",
      label: "UGX",
      description: "Ugandan Shilling",
      flag: "🇺🇬",
    },
    {
      id: "TZS",
      label: "TZS",
      description: "Tanzanian Shilling",
      flag: "🇹🇿",
    },
  ];

  const handleDestinationCurrencyToggle = (currencyId: string) => {
    setSelectedCurrencies((prev) =>
      prev.includes(currencyId)
        ? prev.filter((id) => id !== currencyId)
        : [...prev, currencyId],
    );
  };

  // Convert exchange rates from feature flag to currency rate format
  const currencyRates: CurrencyRate[] = [
    {
      code: "NGN",
      currency: "Nigerian Naira",
      flag: "🇳🇬",
      buyRate: exchangeRates["GBP-NGN"] || 0,
    },
    {
      code: "KES",
      currency: "Kenyan Shilling",
      flag: "🇰🇪",
      buyRate: exchangeRates["GBP-KES"] || 0,
    },
    {
      code: "GHS",
      currency: "Ghanaian Cedi",
      flag: "🇬🇭",
      buyRate: exchangeRates["GBP-GHS"] || 0,
    },
    {
      code: "ZAR",
      currency: "South African Rand",
      flag: "🇿🇦",
      buyRate: exchangeRates["GBP-ZAR"] || 0,
    },
    {
      code: "UGX",
      currency: "Ugandan Shilling",
      flag: "🇺🇬",
      buyRate: exchangeRates["GBP-UGX"] || 0,
    },
    {
      code: "TZS",
      currency: "Tanzanian Shilling",
      flag: "🇹🇿",
      buyRate: exchangeRates["GBP-TZS"] || 0,
    },
  ];

  const calculateConversion = () => {
    if (!amount || !fromCurrency || !toCurrency) {
      setConvertedAmount(null);
      return;
    }

    // Handle same currency conversion
    if (fromCurrency === toCurrency) {
      setConvertedAmount(amount);
      return;
    }

    // Handle base currency conversions (GBP, EUR, USD)
    if (
      baseCurrencyCodes.includes(fromCurrency) &&
      baseCurrencyCodes.includes(toCurrency)
    ) {
      // Direct base currency conversion
      const pairKey =
        `${fromCurrency}-${toCurrency}` as keyof typeof exchangeRates;
      const rate = exchangeRates[pairKey];
      if (rate) {
        const converted = parseFloat(amount) * rate;
        setConvertedAmount(converted.toFixed(2));
      } else {
        setConvertedAmount(null);
      }
      return;
    }

    // Handle conversion to African currencies
    if (
      baseCurrencyCodes.includes(fromCurrency) &&
      !baseCurrencyCodes.includes(toCurrency)
    ) {
      const pairKey =
        `${fromCurrency}-${toCurrency}` as keyof typeof exchangeRates;
      const rate = exchangeRates[pairKey];
      if (rate) {
        const converted = parseFloat(amount) * rate;
        setConvertedAmount(converted.toFixed(2));
      } else {
        setConvertedAmount(null);
      }
      return;
    }

    // Handle conversion from African currencies to base currencies
    if (
      !baseCurrencyCodes.includes(fromCurrency) &&
      baseCurrencyCodes.includes(toCurrency)
    ) {
      const pairKey =
        `${toCurrency}-${fromCurrency}` as keyof typeof exchangeRates;
      const rate = exchangeRates[pairKey];
      if (rate) {
        const converted = parseFloat(amount) / rate;
        setConvertedAmount(converted.toFixed(2));
      } else {
        setConvertedAmount(null);
      }
      return;
    }

    setConvertedAmount(null);
  };

  const onToCurrencyChange = (value: string) => {
    setToCurrency(value);
    analytics.track("fx_to_currency_changed", {
      toCurrency: value,
    });
  };
  const onFromCurrencyChange = (value: string) => {
    setFromCurrency(value);
    analytics.track("fx_from_currency_changed", {
      fromCurrency: value,
    });
  };

  const handleCurrencyConversion = async () => {
    if (!amount || !convertedAmount) return;

    // Pre-populate selected currencies with current destination currency
    setSelectedCurrencies([toCurrency]);

    // Show feedback form instead of processing conversion
    setShowFeedbackForm(true);

    analytics.track("fx_convert_button_clicked", {
      amount,
      fromCurrency,
      toCurrency,
      convertedAmount,
    });
  };

  // Calculate fees and rates
  const getExchangeRate = () => {
    if (fromCurrency === toCurrency) return "1.00";

    const pairKey =
      `${fromCurrency}-${toCurrency}` as keyof typeof exchangeRates;
    const rate = exchangeRates[pairKey];

    if (rate) {
      return rate.toFixed(2);
    }

    // Try reverse pair for African to base currency
    const reversePairKey =
      `${toCurrency}-${fromCurrency}` as keyof typeof exchangeRates;
    const reverseRate = exchangeRates[reversePairKey];
    if (reverseRate) {
      return (1 / reverseRate).toFixed(2);
    }

    return null;
  };

  const calculateFees = () => {
    const amountNum = parseFloat(amount) || 0;

    // Base fee calculation (example: 0.5% of amount)
    const baseFee = amountNum * 0.005;

    return {
      fee: baseFee.toFixed(2),
      totalFee: baseFee.toFixed(2),
      feePercentage: "0.53%", // This would be calculated based on actual rate
    };
  };

  const fees = calculateFees();

  const handleFeedbackSubmit = async () => {
    if (isInterestedInProduct === null) return;

    const feedbackData = {
      originalConversion: {
        amount,
        fromCurrency,
        toCurrency,
        convertedAmount,
      },
      productInterest: isInterestedInProduct,
      destinationCurrencies: selectedCurrencies,
      additionalCurrencies,
      additionalFeedback,
      generalFeedback: additionalFeedback,
    };

    analytics.track("fx_feedback_submit", feedbackData);

    setIsSubmittingFeedback(true);

    try {
      // Reset form and close dialog
      setAdditionalFeedback("");
      setIsInterestedInProduct(null);
      setSelectedCurrencies([]);
      setAdditionalCurrencies("");
      setShowFeedbackForm(false);
      toast.success("Feedback submitted successfully");
    } catch (e: unknown) {
      console.error("Failed to submit feedback:", e);
      toast.error("Failed to submit feedback");
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Calculate conversion when dependencies change
  useEffect(() => {
    calculateConversion();
  }, [amount, fromCurrency, toCurrency, exchangeRates]);

  return (
    <>
      {/* Available Balances Display */}
      <Card className="mb-6 bg-volume-light-purple border-volume-text-purple/20">
        <CardContent className="p-6">
          <h3 className="font-medium text-volume-text-purple mb-4">
            Available Balances
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Major Currencies */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇬🇧</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">GBP Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  £{gbpBalanceAcrossAllAccounts.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇪🇺</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">EUR Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  €{eurBalanceAcrossAllAccounts.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇺🇸</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">USD Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  $0.00
                </p>
              </div>
            </div>

            {/* Exotic Currencies */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇳🇬</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">NGN Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  ₦0.00
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇰🇪</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">KES Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  KSh 0.00
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇬🇭</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">GHS Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  ₵0.00
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇿🇦</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">ZAR Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  R0.00
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇺🇬</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">UGX Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  USh 0.00
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-volume-light-purple rounded-full flex items-center justify-center">
                <span className="text-lg">🇹🇿</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">TZS Balance</p>
                <p className="text-xl font-semibold text-volume-text-purple">
                  TSh 0.00
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>Multi-currency balances ready for conversion and trading</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex min-h-[600px] gap-8">
        {/* Left Panel - Exchange Card */}
        <div className=" flex min-w-1/3">
          <ExchangeCard
            fromCurrency={fromCurrency}
            toCurrency={toCurrency}
            amount={amount}
            convertedAmount={convertedAmount}
            exchangeRate={getExchangeRate()}
            totalFee={fees.totalFee}
            onFromCurrencyChange={onFromCurrencyChange}
            onToCurrencyChange={onToCurrencyChange}
            onAmountChange={setAmount}
            onConvert={handleCurrencyConversion}
            baseCurrencies={baseCurrencies}
            destinationCurrencies={[
              ...baseCurrencies,
              ...currencyRates.map((rate) => ({
                code: rate.code,
                name: rate.currency,
                flag: rate.flag,
              })),
            ]}
          />
        </div>

        {/* Right Panel - Description */}
        <div className="  px-8 py-12 flex flex-col justify-center rounded-lg w-full">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Send simple, seamless payments.
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Save with our stand out exchange rates.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-volume-light-purple rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg
                    className="w-6 h-6 text-volume-text-purple"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div className="text-left">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Live exchange rates
                  </h3>
                  <p className="text-sm text-gray-600">
                    Manage your FX needs directly from your Volume Virtual
                    Account.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-volume-light-purple rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-volume-text-purple"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900 mb-2">
                      No hidden fees
                    </h3>
                    <p className="text-sm text-gray-600">
                      We don't hide any fees. You'll always know exactly what
                      you're paying.
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-volume-light-purple rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-volume-text-purple"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900 mb-2">
                      Seamless transfer
                    </h3>
                    <p className="text-sm text-gray-600">
                      Quick and simple transfers with no delays or
                      complications.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comprehensive Feedback Dialog */}
      <Dialog open={showFeedbackForm} onOpenChange={setShowFeedbackForm}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-volume-light-purple rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-volume-text-purple"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <DialogTitle className="flex items-center gap-2">
                FX Exchange Feature Feedback
                <span className="text-xs bg-volume-text-purple text-white px-2 py-1 rounded-full font-medium">
                  NEW FEATURE
                </span>
              </DialogTitle>
            </div>
            <DialogDescription>
              This is a new feature that's not fully rolled out yet. Help us
              understand your FX needs and prioritize which currencies to
              support first.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current Selection Display */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">
                Your Current Selection
              </h4>
              <p className="text-sm text-blue-700">
                Converting {amount} {fromCurrency} to {toCurrency}
                {convertedAmount && ` (≈ ${convertedAmount} ${toCurrency})`}
              </p>
            </div>

            {/* Product Interest Question */}
            <div className="">
              <Label className="text-sm font-medium text-gray-700">
                Are you interested in using FX exchange services? (Required)
              </Label>
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsInterestedInProduct(true)}
                  className={
                    isInterestedInProduct === true
                      ? "bg-green-600 hover:bg-green-700 border-green-600 text-white hover:border-green-700"
                      : ""
                  }
                >
                  <Check className="w-4 h-4 mr-1" />
                  Yes, very interested
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsInterestedInProduct(false)}
                  className={
                    isInterestedInProduct === false
                      ? "bg-red-600 hover:bg-red-700 border-red-600 text-white hover:border-red-700"
                      : ""
                  }
                >
                  <X className="w-4 h-4 mr-1" />
                  Not interested
                </Button>
              </div>
            </div>

            {/* Destination Currencies Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Which destination currencies are you most interested in? (Select
                all that apply) (Optional)
              </Label>
              <div className="grid grid-cols-2 gap-2 h-56 overflow-y-auto border rounded-lg p-3">
                {destinationCurrencies.map((currency) => (
                  <div
                    key={currency.id}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={currency.id}
                      checked={selectedCurrencies.includes(currency.id)}
                      onCheckedChange={() =>
                        handleDestinationCurrencyToggle(currency.id)
                      }
                    />
                    <Label
                      htmlFor={currency.id}
                      className="text-sm cursor-pointer flex-1"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{currency.flag}</span>
                        <div>
                          <div className="font-medium">{currency.label}</div>
                          <div className="text-xs text-gray-500">
                            {currency.description}
                          </div>
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Currencies Question */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Additional currencies that you would require (Optional)
              </Label>
              <Input
                value={additionalCurrencies}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAdditionalCurrencies(e.target.value)
                }
                placeholder="Enter any additional currencies you would require."
                className="w-full my-0"
              />
            </div>

            {/* Additional Feedback */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Any additional feedback or specific requirements? (Optional)
              </Label>
              <Textarea
                value={additionalFeedback}
                onChange={(e) => setAdditionalFeedback(e.target.value)}
                placeholder="Tell us about your FX needs, pain points with current solutions, or any specific features you'd like to see..."
                className="min-h-[100px] resize-none"
              />
            </div>

            <p className="text-xs text-gray-500">
              Your feedback will help us prioritize features and improve the FX
              exchange experience.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowFeedbackForm(false);
                setAdditionalFeedback("");
                setIsInterestedInProduct(null);
                setSelectedCurrencies([]);
                setAdditionalCurrencies("");
              }}
              disabled={isSubmittingFeedback}
              className="gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              onClick={handleFeedbackSubmit}
              disabled={isInterestedInProduct === null || isSubmittingFeedback}
              className="bg-volume-text-purple hover:bg-volume-text-purple/90 gap-2"
            >
              {isSubmittingFeedback ? (
                <>
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Feedback
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
